import { resolveApiErrorMessage } from './api-errors';
import { authPublicApi } from './auth-public.api';
import { t } from './i18n';

const TOKEN_KEY = 'rc_token';
const USER_KEY = 'rc_user';
const LOGOUT_NOTICE_KEY = 'rc_logout_notice';
const POST_LOGIN_REDIRECT_KEY = 'rc_post_login_redirect';
const POST_ONBOARDING_REDIRECT_KEY = 'rc_post_onboarding_redirect';
const REQUESTED_ACCOUNT_MODE_KEY = 'rc_requested_account_mode';
const PRICING_CHECKOUT_RESUME_PATH = '/pricing/?checkout=resume';
const PUBLIC_IP_LOOKUP_URL = 'https://api.ipify.org?format=json';
const SIGNUP_DISABLED_CODE = 'signup_disabled';
const PLATFORM_COUNTRY_NOT_ALLOWED_CODE = 'platform_country_not_allowed';
const AUTH_CHANGE_EVENT = 'rc:authchange';
const PRICING_CHECKOUT_RESUME_INTENT = 'pricing_checkout_resume';

export interface UserInfo {
  email: string;
  name: string;
  provider: string;
  accountMode?: 'landlord' | 'tenant';
  onboardingCompleted?: boolean;
}

export interface SignupDisabledDetails {
  email: string;
  notificationToken: string;
}

class AuthService {
  private token: string | null = null;
  private user: UserInfo | null = null;
  private listeners: Array<(authenticated: boolean) => void> = [];
  private loginStateListeners: Array<(loading: boolean) => void> = [];
  private loginError = '';
  private loginErrorCode = '';
  private loginSuccess = '';
  private signupDisabledDetails: SignupDisabledDetails | null = null;
  private readonly apiBaseUrl: string;
  private _isLoggingIn = false;
  private clientIpAddressPromise: Promise<string | null> | null = null;

  constructor() {
    this.apiBaseUrl = (window as any).__RC_API_BASE__ || 'http://localhost:7071/api';
    this.token = localStorage.getItem(TOKEN_KEY);
    const userJson = localStorage.getItem(USER_KEY);
    if (userJson) {
      const parsed = JSON.parse(userJson) as Partial<UserInfo>;
      this.user = {
        email: parsed.email || '',
        name: parsed.name || '',
        provider: parsed.provider || 'google',
        accountMode: parsed.accountMode === 'tenant' ? 'tenant' : 'landlord',
        onboardingCompleted: parsed.onboardingCompleted === true,
      };
    }
  }

  get isAuthenticated(): boolean {
    return this.token !== null;
  }

  get currentUser(): UserInfo | null {
    return this.user;
  }

  get accessToken(): string | null {
    return this.token;
  }

  get currentLoginError(): string {
    return this.loginError;
  }

  get currentLoginErrorCode(): string {
    return this.loginErrorCode;
  }

  get currentSignupDisabledDetails(): SignupDisabledDetails | null {
    return this.signupDisabledDetails;
  }

  get currentLoginSuccess(): string {
    return this.loginSuccess;
  }

  get currentLogoutNoticeCode(): string {
    return sessionStorage.getItem(LOGOUT_NOTICE_KEY) || '';
  }

  clearLoginError(): void {
    this.setLoginError('');
  }

  dismissLogoutNotice(): void {
    sessionStorage.removeItem(LOGOUT_NOTICE_KEY);
  }

  get isAdmin(): boolean {
    if (!this.token) return false;
    try {
      const payload = JSON.parse(atob(this.token.split('.')[1]));
      return payload['role'] === '1';
    } catch {
      return false;
    }
  }

  onAuthChange(listener: (authenticated: boolean) => void): void {
    this.listeners.push(listener);
  }

  get isLoggingIn(): boolean {
    return this._isLoggingIn;
  }

  onLoginStateChange(listener: (loading: boolean) => void): void {
    this.loginStateListeners.push(listener);
  }

  private setLoggingIn(value: boolean): void {
    this._isLoggingIn = value;
    this.loginStateListeners.forEach(l => l(value));
  }

  private setLoginError(message: string, code = '', details: SignupDisabledDetails | null = null): void {
    this.loginError = message;
    this.loginErrorCode = code;
    this.loginSuccess = '';
    this.signupDisabledDetails = code === SIGNUP_DISABLED_CODE ? details : null;
    this.loginStateListeners.forEach(l => l(this._isLoggingIn));
  }

  private setLoginSuccess(message: string): void {
    this.loginError = '';
    this.loginErrorCode = '';
    this.loginSuccess = message;
    this.signupDisabledDetails = null;
    this.loginStateListeners.forEach(l => l(this._isLoggingIn));
  }

  private resolveLoginError(error: { error?: string; code?: string } | null | undefined, fallbackKey: string): string {
    return resolveApiErrorMessage(error, 403, fallbackKey);
  }

  private notify(): void {
    this.listeners.forEach(l => l(this.isAuthenticated));
    globalThis.window?.dispatchEvent?.(new CustomEvent(AUTH_CHANGE_EVENT, {
      detail: {
        authenticated: this.isAuthenticated,
        user: this.user,
      },
    }));
  }

  private async getClientIpAddress(): Promise<string | null> {
    this.clientIpAddressPromise ??= fetch(PUBLIC_IP_LOOKUP_URL)
      .then(async response => {
        if (!response.ok) {
          return null;
        }

        const payload = await response.json().catch(() => null) as { ip?: string } | null;
        const ipAddress = payload?.ip?.trim();
        return ipAddress || null;
      })
      .catch(() => null);

    return this.clientIpAddressPromise;
  }

  rememberRequestedAccountMode(accountMode: 'landlord' | 'tenant' | null): void {
    if (!accountMode) {
      sessionStorage.removeItem(REQUESTED_ACCOUNT_MODE_KEY);
      return;
    }

    sessionStorage.setItem(REQUESTED_ACCOUNT_MODE_KEY, accountMode);
  }

  private consumeRequestedAccountMode(): 'landlord' | 'tenant' | null {
    const requestedAccountMode = sessionStorage.getItem(REQUESTED_ACCOUNT_MODE_KEY);
    sessionStorage.removeItem(REQUESTED_ACCOUNT_MODE_KEY);
    return requestedAccountMode === 'tenant' || requestedAccountMode === 'landlord'
      ? requestedAccountMode
      : null;
  }

  setSession(token: string, user: UserInfo): void {
    this.token = token;
    this.user = user;
    this.loginError = '';
    this.loginErrorCode = '';
    this.loginSuccess = '';
    this.signupDisabledDetails = null;
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    sessionStorage.removeItem(REQUESTED_ACCOUNT_MODE_KEY);
    this.notify();
  }

  updateCurrentUserName(name: string): void {
    if (!this.user) return;
    this.user = { ...this.user, name };
    localStorage.setItem(USER_KEY, JSON.stringify(this.user));
    this.notify();
  }

  updateCurrentUser(partial: Partial<UserInfo>): void {
    if (!this.user) return;
    this.user = { ...this.user, ...partial };
    localStorage.setItem(USER_KEY, JSON.stringify(this.user));
    this.notify();
  }

  logout(): void {
    this.dismissLogoutNotice();
    this.token = null;
    this.user = null;
    this.loginError = '';
    this.loginErrorCode = '';
    this.loginSuccess = '';
    this.signupDisabledDetails = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(REQUESTED_ACCOUNT_MODE_KEY);
    this.notify();
    window.location.href = '/login/';
  }

  logoutWithNotice(code: string): void {
    if (code === PLATFORM_COUNTRY_NOT_ALLOWED_CODE) {
      sessionStorage.setItem(LOGOUT_NOTICE_KEY, code);
    } else {
      this.dismissLogoutNotice();
    }

    this.token = null;
    this.user = null;
    this.loginError = '';
    this.loginErrorCode = '';
    this.loginSuccess = '';
    this.signupDisabledDetails = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(REQUESTED_ACCOUNT_MODE_KEY);
    this.notify();
    window.location.href = '/login/';
  }

  getDefaultAuthenticatedPath(): string {
    if (this.isAdmin) {
      return '/admin/';
    }

    if (this.user?.accountMode === 'tenant') {
      return this.user.onboardingCompleted === false
        ? '/onboarding/'
        : '/tenant/active/';
    }

    return this.user?.onboardingCompleted === true
      ? '/dashboard/'
      : '/onboarding/';
  }

  redirectAuthenticatedUser(fallbackPath = '/onboarding/'): void {
    window.location.href = this.resolvePostLoginRedirect(fallbackPath);
  }

  rememberPostLoginRedirect(path: string): void {
    const normalizedPath = path.trim();
    if (!normalizedPath) {
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
      return;
    }

    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, normalizedPath);
  }

  rememberPostOnboardingRedirect(path: string): void {
    const normalizedPath = path.trim();
    if (!normalizedPath) {
      sessionStorage.removeItem(POST_ONBOARDING_REDIRECT_KEY);
      return;
    }

    sessionStorage.setItem(POST_ONBOARDING_REDIRECT_KEY, normalizedPath);
  }

  peekPostOnboardingRedirect(fallbackPath = '/tenant/active/'): string {
    return sessionStorage.getItem(POST_ONBOARDING_REDIRECT_KEY) || fallbackPath;
  }

  private resolveExplicitLoginRedirect(): string | null {
    if (window.location.pathname !== '/login/') {
      return null;
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'resume') {
      return PRICING_CHECKOUT_RESUME_PATH;
    }

    return null;
  }

  private resolveRequestedPostLoginIntent(): 'pricing_checkout_resume' | null {
    return this.resolveExplicitLoginRedirect() === PRICING_CHECKOUT_RESUME_PATH
      ? PRICING_CHECKOUT_RESUME_INTENT
      : null;
  }

  resolvePostLoginRedirect(fallbackPath = '/onboarding/'): string {
    const explicitRedirect = this.resolveExplicitLoginRedirect();
    if (explicitRedirect) {
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
      return explicitRedirect;
    }

    const redirectPath = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    if (window.location.pathname === '/login/' && redirectPath === PRICING_CHECKOUT_RESUME_PATH) {
      return fallbackPath;
    }

    return redirectPath || fallbackPath;
  }

  resolvePostOnboardingRedirect(fallbackPath = '/tenant/active/'): string {
    const redirectPath = sessionStorage.getItem(POST_ONBOARDING_REDIRECT_KEY);
    sessionStorage.removeItem(POST_ONBOARDING_REDIRECT_KEY);
    return redirectPath || fallbackPath;
  }

  async initGoogleLogin(clientId: string): Promise<void> {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.onload = () => {
        const client = (window as any).google.accounts.oauth2.initCodeClient({
          client_id: clientId,
          scope: 'openid email profile',
          ux_mode: 'popup',
          callback: (response: any) => {
            this.handleGoogleCodeResponse(response);
          },
        });
        (window as any)._googleCodeClient = client;
        resolve();
      };
      document.head.appendChild(script);
    });
  }

  loginWithGoogle(): void {
    this.clearLoginError();
    this.setLoggingIn(true);
    (window as any)._googleCodeClient?.requestCode();
  }

  private async handleGoogleCodeResponse(response: any): Promise<void> {
    if (response.error) {
      console.error('Google login error:', response.error);
      this.setLoginError(t('auth.googleLoginFailed'));
      this.setLoggingIn(false);
      return;
    }

    try {
      const clientIpAddress = await this.getClientIpAddress();
      const requestedAccountMode = this.consumeRequestedAccountMode();

      // Exchange the authorization code for tokens via our API
      const res = await fetch(`${this.apiBaseUrl}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: response.code, clientIpAddress, requestedAccountMode }),
      });

      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: t('auth.googleLoginFailed') }));
        console.error('Token exchange failed');
        this.setLoginError(
          this.resolveLoginError(error, 'auth.googleLoginFailed'),
          error?.code || '',
          this.resolveSignupDisabledDetails(error));
        this.setLoggingIn(false);
        return;
      }

      const data = await res.json();
      this.setSession(data.token, {
        email: data.email,
        name: data.displayName,
        provider: 'google',
        accountMode: data.accountMode === 'tenant' ? 'tenant' : 'landlord',
        onboardingCompleted: data.onboardingCompleted === true,
      });
      this.redirectAuthenticatedUser(this.getDefaultAuthenticatedPath());
    } catch (error) {
      console.error('Google login failed:', error);
      this.setLoginError(t('auth.googleLoginFailed'));
      this.setLoggingIn(false);
    }
  }

  async initMicrosoftLogin(clientId: string): Promise<void> {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://alcdn.msauth.net/browser/2.38.0/js/msal-browser.min.js';
      script.onload = () => {
        const msalConfig = {
          auth: {
            clientId,
            authority: 'https://login.microsoftonline.com/common',
            redirectUri: window.location.origin,
          },
        };
        (window as any)._msalInstance = new (window as any).msal.PublicClientApplication(msalConfig);
        resolve();
      };
      document.head.appendChild(script);
    });
  }

  async loginWithMicrosoft(): Promise<void> {
    const msalInstance = (window as any)._msalInstance;
    if (!msalInstance) return;

    this.clearLoginError();
    this.setLoggingIn(true);
    try {
      const loginResponse = await msalInstance.loginPopup({
        scopes: ['openid', 'profile', 'email'],
      });

      const clientIpAddress = await this.getClientIpAddress();
      const requestedAccountMode = this.consumeRequestedAccountMode();

      const res = await fetch(`${this.apiBaseUrl}/auth/microsoft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken: loginResponse.idToken, clientIpAddress, requestedAccountMode }),
      });

      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: t('auth.microsoftLoginFailed') }));
        console.error('Microsoft token exchange failed');
        this.setLoginError(
          this.resolveLoginError(error, 'auth.microsoftLoginFailed'),
          error?.code || '',
          this.resolveSignupDisabledDetails(error));
        this.setLoggingIn(false);
        return;
      }

      const data = await res.json();

      this.setSession(data.token, {
        email: data.email,
        name: data.displayName,
        provider: 'microsoft',
        accountMode: data.accountMode === 'tenant' ? 'tenant' : 'landlord',
        onboardingCompleted: data.onboardingCompleted === true,
      });
      this.redirectAuthenticatedUser(this.getDefaultAuthenticatedPath());
    } catch (error) {
      console.error('Microsoft login failed:', error);
      this.setLoginError(t('auth.microsoftLoginFailed'));
      this.setLoggingIn(false);
    }
  }

  async requestEmailLogin(email: string): Promise<void> {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      this.setLoginError(t('auth.emailRequired'));
      return;
    }

    this.clearLoginError();
    this.setLoggingIn(true);

    try {
      const clientIpAddress = await this.getClientIpAddress();
      const requestedAccountMode = this.consumeRequestedAccountMode();
      await authPublicApi.requestEmailLogin({
        email: normalizedEmail,
        preferredLocale: document.documentElement.lang || 'en',
        clientIpAddress,
        requestedAccountMode,
        postLoginIntent: this.resolveRequestedPostLoginIntent(),
      });
      this.setLoginSuccess(t('auth.emailLinkSent'));
    } catch (error: any) {
      this.setLoginError(error?.message || t('auth.emailLoginFailed'));
    } finally {
      this.setLoggingIn(false);
    }
  }

  async completeEmailLogin(token: string): Promise<void> {
    const normalizedToken = token.trim();
    if (!normalizedToken) {
      this.setLoginError(t('auth.emailLoginFailed'));
      return;
    }

    this.clearLoginError();
    this.setLoggingIn(true);

    try {
      const clientIpAddress = await this.getClientIpAddress();
      const data = await authPublicApi.completeEmailLogin({
        token: normalizedToken,
        clientIpAddress,
      });

      this.setSession(data.token, {
        email: data.email,
        name: data.displayName,
        provider: this.user?.provider || 'email',
        accountMode: data.accountMode === 'tenant' ? 'tenant' : 'landlord',
        onboardingCompleted: data.onboardingCompleted === true,
      });
      if (data.postLoginIntent === PRICING_CHECKOUT_RESUME_INTENT) {
        this.redirectAuthenticatedUser(PRICING_CHECKOUT_RESUME_PATH);
        return;
      }

      this.redirectAuthenticatedUser(this.getDefaultAuthenticatedPath());
    } catch (error: any) {
      this.setLoginError(error?.message || t('auth.emailLoginFailed'));
      this.setLoggingIn(false);
    }
  }

  private resolveSignupDisabledDetails(error: any): SignupDisabledDetails | null {
    const email = error?.details?.email?.trim();
    const notificationToken = error?.details?.notificationToken?.trim();
    if (!email || !notificationToken) {
      return null;
    }

    return { email, notificationToken };
  }
}

export const authService = new AuthService();
export const AUTH_CHANGE_EVENT_NAME = AUTH_CHANGE_EVENT;
